import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  listarTickets,
  crearTicket,
  obtenerTicket,
  actualizarEstadoTicket,
  comentarTicket,
  obtenerBodegas,
  obtenerAreas,
} from '../../utils/api';
import toast from 'react-hot-toast';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

// ─────────────────────────────────────────
// CATÁLOGOS
// ─────────────────────────────────────────
const CATEGORIAS = [
  { value: 'urgente',                    label: 'Urgente',              icon: '🚨', color: '#EF4444', bg: '#FEE2E2' },
  { value: 'mantenimiento_equipos',      label: 'Equipos',              icon: '⚙️',  color: '#F59E0B', bg: '#FEF3C7' },
  { value: 'mantenimiento_electrico',    label: 'Eléctrico / Luces',    icon: '💡', color: '#6366F1', bg: '#EEF2FF' },
  { value: 'mantenimiento_infraestructura', label: 'Infraestructura',   icon: '🏗️',  color: '#8B5CF6', bg: '#F5F3FF' },
  { value: 'pqrs_cliente',               label: 'PQRS Cliente',         icon: '💬', color: '#0EA5E9', bg: '#E0F2FE' },
];

const ESTADOS = [
  { value: 'abierto',     label: 'Abierto',      color: '#EF4444', bg: '#FEE2E2' },
  { value: 'en_proceso',  label: 'En proceso',   color: '#F59E0B', bg: '#FEF3C7' },
  { value: 'resuelto',    label: 'Resuelto',     color: '#10B981', bg: '#D1FAE5' },
  { value: 'cerrado',     label: 'Cerrado',      color: '#6B7280', bg: '#F3F4F6' },
];

const PRIORIDADES = [
  { value: 'baja',    label: 'Baja',    color: '#10B981' },
  { value: 'normal',  label: 'Normal',  color: '#6B7280' },
  { value: 'alta',    label: 'Alta',    color: '#F59E0B' },
  { value: 'urgente', label: 'Urgente', color: '#EF4444' },
];

const getCat   = (v) => CATEGORIAS.find(c => c.value === v) || CATEGORIAS[1];
const getEst   = (v) => ESTADOS.find(e => e.value === v) || ESTADOS[0];
const getPrio  = (v) => PRIORIDADES.find(p => p.value === v) || PRIORIDADES[1];

const relTime = (d) => formatDistanceToNow(new Date(d), { addSuffix: true, locale: es });

// ─────────────────────────────────────────
// BADGE GENÉRICO
// ─────────────────────────────────────────
function Badge({ label, color, bg, size = 'sm' }) {
  return (
    <span style={{
      background: bg, color,
      padding: size === 'sm' ? '3px 9px' : '5px 12px',
      borderRadius: 20,
      fontSize: size === 'sm' ? 11 : 13,
      fontWeight: 700,
      display: 'inline-block',
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

// ─────────────────────────────────────────
// TARJETA DE TICKET
// ─────────────────────────────────────────
function TarjetaTicket({ ticket, onClick, activo }) {
  const cat  = getCat(ticket.categoria);
  const est  = getEst(ticket.estado);
  const prio = getPrio(ticket.prioridad);
  const esUrgente = ticket.prioridad === 'urgente' || ticket.categoria === 'urgente';

  return (
    <div
      onClick={() => onClick(ticket)}
      style={{
        ...tk.card,
        borderLeft: `4px solid ${cat.color}`,
        background: activo ? '#F0F7FF' : 'white',
        boxShadow: activo
          ? '0 0 0 2px #3B82F6, 0 2px 8px rgba(59,130,246,0.15)'
          : '0 1px 4px rgba(0,0,0,0.07)',
        ...(esUrgente ? { animation: 'pulseRed 2s infinite' } : {}),
      }}
    >
      {esUrgente && <div style={tk.urgentStripe} />}

      <div style={tk.top}>
        <span style={tk.numero}>{ticket.numero}</span>
        <span style={tk.icon}>{cat.icon}</span>
        <div style={tk.badges}>
          <Badge label={est.label} color={est.color} bg={est.bg} />
          {ticket.prioridad === 'urgente' && <Badge label="⚡ URGENTE" color="#EF4444" bg="#FEE2E2" />}
        </div>
      </div>

      <div style={tk.titulo}>{ticket.titulo}</div>

      <div style={tk.meta}>
        <span style={tk.area}>🏪 {ticket.bodega_nombre || ticket.area_nombre || '—'}</span>
        <span style={tk.tiempo}>{relTime(ticket.created_at)}</span>
      </div>
    </div>
  );
}

const tk = {
  card: {
    borderRadius: 12, padding: '14px 16px', cursor: 'pointer',
    transition: 'all 0.15s', position: 'relative', overflow: 'hidden',
  },
  urgentStripe: {
    position: 'absolute', top: 0, right: 0,
    width: 0, height: 0,
    borderStyle: 'solid',
    borderWidth: '0 24px 24px 0',
    borderColor: 'transparent #EF4444 transparent transparent',
  },
  top: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' },
  numero: { fontSize: 11, fontWeight: 700, color: '#9CA3AF', fontFamily: 'monospace' },
  icon: { fontSize: 16 },
  badges: { display: 'flex', gap: 4, flexWrap: 'wrap', marginLeft: 'auto' },
  titulo: { fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 8, lineHeight: 1.3 },
  meta: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  area: { fontSize: 11, color: '#6B7280' },
  tiempo: { fontSize: 11, color: '#9CA3AF' },
};

// ─────────────────────────────────────────
// DETALLE DE TICKET
// ─────────────────────────────────────────
function DetalleTicket({ ticketId, onClose, onUpdated, esAdmin }) {
  const [data, setData]         = useState(null);
  const [cargando, setCargando] = useState(true);
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);
  const comentariosRef = useRef(null);

  const cargar = () => {
    setCargando(true);
    obtenerTicket(ticketId)
      .then(r => setData(r.data))
      .catch(() => toast.error('Error cargando ticket'))
      .finally(() => setCargando(false));
  };

  useEffect(() => { cargar(); }, [ticketId]);

  useEffect(() => {
    if (comentariosRef.current) {
      comentariosRef.current.scrollTop = comentariosRef.current.scrollHeight;
    }
  }, [data?.comentarios]);

  const cambiarEstado = async (nuevoEstado) => {
    try {
      await actualizarEstadoTicket(ticketId, { estado: nuevoEstado });
      toast.success('Estado actualizado');
      cargar();
      onUpdated();
    } catch { toast.error('Error actualizando estado'); }
  };

  const enviarComentario = async () => {
    if (!comentario.trim()) return;
    setEnviando(true);
    try {
      await comentarTicket(ticketId, comentario.trim());
      setComentario('');
      cargar();
    } catch { toast.error('Error enviando comentario'); }
    finally { setEnviando(false); }
  };

  if (cargando) return (
    <div style={dt.root}>
      <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Cargando ticket...</div>
    </div>
  );

  if (!data) return null;

  const { ticket, comentarios } = data;
  const cat  = getCat(ticket.categoria);
  const est  = getEst(ticket.estado);

  return (
    <div style={dt.root}>
      {/* Header */}
      <div style={{ ...dt.header, borderLeft: `4px solid ${cat.color}` }}>
        <div style={dt.headerTop}>
          <span style={dt.numero}>{ticket.numero}</span>
          <button onClick={onClose} style={dt.closeBtn}>✕</button>
        </div>

        <h2 style={dt.titulo}>{ticket.titulo}</h2>

        <div style={dt.metaRow}>
          <span style={dt.catBadge}>{cat.icon} {cat.label}</span>
          <Badge label={est.label} color={est.color} bg={est.bg} size="md" />
          <Badge label={getPrio(ticket.prioridad).label} color={getPrio(ticket.prioridad).color} bg="#F9FAFB" size="md" />
        </div>

        <div style={dt.infoRow}>
          <span>📍 {ticket.bodega_nombre || ticket.area_nombre || '—'}</span>
          <span>👤 {ticket.creado_por_nombre}</span>
          <span>🕐 {ticket.created_at ? format(new Date(ticket.created_at), "d MMM yyyy, HH:mm", { locale: es }) : '—'}</span>
          {ticket.fecha_limite && <span>📅 Límite: {format(new Date(ticket.fecha_limite.slice(0,10) + 'T12:00'), "d MMM", { locale: es })}</span>}
        </div>
      </div>

      {/* Descripción */}
      <div style={dt.desc}>
        <div style={dt.descLabel}>Descripción</div>
        <p style={dt.descText}>{ticket.descripcion}</p>
      </div>

      {/* Cambiar estado (admin) */}
      {esAdmin && ticket.estado !== 'cerrado' && (
        <div style={dt.estadosSection}>
          <div style={dt.descLabel}>Cambiar estado</div>
          <div style={dt.estadosBtns}>
            {ESTADOS.filter(e => e.value !== ticket.estado).map(e => (
              <button
                key={e.value}
                onClick={() => cambiarEstado(e.value)}
                style={{ ...dt.estadoBtn, borderColor: e.color, color: e.color }}
              >
                {e.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Comentarios */}
      <div style={dt.comentariosSection}>
        <div style={dt.descLabel}>Actividad ({comentarios.length})</div>
        <div ref={comentariosRef} style={dt.comentariosList}>
          {comentarios.length === 0 && (
            <div style={dt.sinComentarios}>Sin comentarios aún. Sé el primero en escribir.</div>
          )}
          {comentarios.map((c, i) => (
            <div key={i} style={dt.comentario}>
              <div style={dt.comAvatar}>{c.usuario_nombre?.charAt(0).toUpperCase()}</div>
              <div style={dt.comBody}>
                <div style={dt.comHeader}>
                  <strong>{c.usuario_nombre}</strong>
                  <span style={dt.comArea}>{c.area_nombre}</span>
                  <span style={dt.comTiempo}>{relTime(c.created_at)}</span>
                </div>
                <p style={dt.comTexto}>{c.comentario}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Input comentario */}
        {ticket.estado !== 'cerrado' && (
          <div style={dt.comInput}>
            <textarea
              value={comentario}
              onChange={e => setComentario(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) enviarComentario(); }}
              placeholder="Escribe un comentario... (Ctrl+Enter para enviar)"
              rows={3}
              style={dt.comTextarea}
            />
            <button
              onClick={enviarComentario}
              disabled={enviando || !comentario.trim()}
              style={{ ...dt.comBtn, opacity: (!comentario.trim() || enviando) ? 0.5 : 1 }}
            >
              {enviando ? '⏳' : '➤ Enviar'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const dt = {
  root: { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
  header: { padding: '20px 24px', background: 'white', borderBottom: '1px solid #F3F4F6', flexShrink: 0 },
  headerTop: { display: 'flex', justifyContent: 'space-between', marginBottom: 8 },
  numero: { fontSize: 12, fontWeight: 700, color: '#9CA3AF', fontFamily: 'monospace' },
  closeBtn: { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6B7280', padding: '0 4px' },
  titulo: { margin: '0 0 12px', fontSize: 18, fontWeight: 800, color: '#111827', lineHeight: 1.3 },
  metaRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' },
  catBadge: { fontSize: 13, fontWeight: 700, color: '#374151' },
  infoRow: { display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: '#6B7280' },
  desc: { padding: '16px 24px', background: '#FAFAFA', borderBottom: '1px solid #F3F4F6', flexShrink: 0 },
  descLabel: { fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 },
  descText: { margin: 0, fontSize: 14, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' },
  estadosSection: { padding: '14px 24px', borderBottom: '1px solid #F3F4F6', flexShrink: 0 },
  estadosBtns: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 },
  estadoBtn: { padding: '7px 14px', border: '2px solid', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.15s' },
  comentariosSection: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '16px 24px 0' },
  comentariosList: { flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14, paddingBottom: 8 },
  sinComentarios: { color: '#9CA3AF', fontSize: 13, textAlign: 'center', padding: '20px 0' },
  comentario: { display: 'flex', gap: 10 },
  comAvatar: {
    width: 32, height: 32, borderRadius: '50%', background: '#1a1a2e',
    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700, flexShrink: 0,
  },
  comBody: { flex: 1 },
  comHeader: { display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 4, flexWrap: 'wrap' },
  comArea: { fontSize: 11, color: '#9CA3AF' },
  comTiempo: { fontSize: 11, color: '#9CA3AF', marginLeft: 'auto' },
  comTexto: { margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.5, background: '#F9FAFB', padding: '8px 12px', borderRadius: '0 10px 10px 10px', whiteSpace: 'pre-wrap' },
  comInput: { borderTop: '1px solid #F3F4F6', paddingTop: 12, paddingBottom: 16, flexShrink: 0, display: 'flex', gap: 8 },
  comTextarea: { flex: 1, padding: '10px 14px', border: '2px solid #E5E7EB', borderRadius: 10, fontSize: 14, resize: 'none', outline: 'none', fontFamily: 'inherit' },
  comBtn: { padding: '0 18px', background: '#1a1a2e', color: 'white', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', alignSelf: 'flex-end', height: 40 },
};

// ─────────────────────────────────────────
// MODAL NUEVO TICKET
// ─────────────────────────────────────────
function ModalNuevoTicket({ bodegas, areas, onClose, onCreado }) {
  const { usuario } = useAuth();
  const [form, setForm] = useState({
    categoria: '',
    titulo: '',
    descripcion: '',
    prioridad: 'normal',
    bodega_id: '',
    area_id: usuario?.area_id || '',
    fecha_limite: '',
  });
  const [guardando, setGuardando] = useState(false);

  const setF = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value || e }));

  const handleCrear = async () => {
    if (!form.categoria) { toast.error('Selecciona una categoría'); return; }
    if (!form.titulo.trim()) { toast.error('El título es requerido'); return; }
    if (!form.descripcion.trim()) { toast.error('La descripción es requerida'); return; }

    // Urgentes siempre tienen prioridad urgente
    const payload = {
      ...form,
      prioridad: form.categoria === 'urgente' ? 'urgente' : form.prioridad,
      bodega_id: form.bodega_id || null,
      area_id: form.area_id || null,
      fecha_limite: form.fecha_limite || null,
    };

    setGuardando(true);
    try {
      await crearTicket(payload);
      toast.success('¡Novedad creada!');
      onCreado();
      onClose();
    } catch { toast.error('Error al crear novedad'); }
    finally { setGuardando(false); }
  };

  const catSeleccionada = getCat(form.categoria);

  return (
    <div style={mn.overlay}>
      <div style={mn.modal}>
        <div style={mn.header}>
          <h3 style={mn.title}>📋 Nueva Novedad</h3>
          <button onClick={onClose} style={mn.closeBtn}>✕</button>
        </div>

        <div style={mn.body}>
          {/* Categoría */}
          <div style={mn.field}>
            <label style={mn.label}>Tipo de novedad *</label>
            <div style={mn.catGrid}>
              {CATEGORIAS.map(c => (
                <button
                  key={c.value}
                  onClick={() => setForm(f => ({ ...f, categoria: c.value }))}
                  style={{
                    ...mn.catBtn,
                    borderColor: form.categoria === c.value ? c.color : '#E5E7EB',
                    background: form.categoria === c.value ? c.bg : 'white',
                    color: form.categoria === c.value ? c.color : '#6B7280',
                  }}
                >
                  <span style={{ fontSize: 20 }}>{c.icon}</span>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>{c.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Prioridad (solo si no es urgente) */}
          {form.categoria && form.categoria !== 'urgente' && (
            <div style={mn.field}>
              <label style={mn.label}>Prioridad</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {PRIORIDADES.filter(p => p.value !== 'urgente').map(p => (
                  <button
                    key={p.value}
                    onClick={() => setForm(f => ({ ...f, prioridad: p.value }))}
                    style={{
                      ...mn.prioBtn,
                      borderColor: form.prioridad === p.value ? p.color : '#E5E7EB',
                      color: form.prioridad === p.value ? p.color : '#6B7280',
                      fontWeight: form.prioridad === p.value ? 700 : 400,
                    }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Título */}
          <div style={mn.field}>
            <label style={mn.label}>Título *</label>
            <input
              value={form.titulo}
              onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
              style={mn.input}
              placeholder="Describe brevemente el problema"
              maxLength={255}
            />
          </div>

          {/* Descripción */}
          <div style={mn.field}>
            <label style={mn.label}>Descripción detallada *</label>
            <textarea
              value={form.descripcion}
              onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
              style={mn.textarea}
              placeholder="Explica el problema con el mayor detalle posible: cuándo ocurrió, cómo afecta la operación, qué ya se intentó..."
              rows={4}
            />
          </div>

          {/* Ubicación y fecha */}
          <div style={mn.dosCols}>
            <div style={mn.field}>
              <label style={mn.label}>Punto de Venta / Bodega</label>
              <select value={form.bodega_id} onChange={e => setForm(f => ({ ...f, bodega_id: e.target.value }))} style={mn.select}>
                <option value="">General / Sin ubicación</option>
                {bodegas.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
              </select>
            </div>
            {form.categoria !== 'urgente' && (
              <div style={mn.field}>
                <label style={mn.label}>Fecha límite (opcional)</label>
                <input
                  type="date"
                  value={form.fecha_limite}
                  onChange={e => setForm(f => ({ ...f, fecha_limite: e.target.value }))}
                  min={format(new Date(), 'yyyy-MM-dd')}
                  style={mn.input}
                />
              </div>
            )}
          </div>

          {/* Alerta urgente */}
          {form.categoria === 'urgente' && (
            <div style={mn.alertaUrgente}>
              🚨 Esta novedad será notificada de <strong>inmediato</strong> a Administración y Gerencia.
            </div>
          )}
        </div>

        <div style={mn.footer}>
          <button onClick={onClose} style={mn.btnSec}>Cancelar</button>
          <button
            onClick={handleCrear}
            disabled={guardando || !form.categoria || !form.titulo || !form.descripcion}
            style={{
              ...mn.btnPri,
              background: catSeleccionada?.color
                ? `linear-gradient(135deg, ${catSeleccionada.color}, ${catSeleccionada.color}cc)`
                : 'linear-gradient(135deg,#1a1a2e,#16213e)',
              opacity: (!form.categoria || !form.titulo || !form.descripcion) ? 0.5 : 1,
            }}
          >
            {guardando ? '⏳ Creando...' : '✓ Crear Novedad'}
          </button>
        </div>
      </div>
    </div>
  );
}

const mn = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
  modal: { background: 'white', borderRadius: 16, width: '100%', maxWidth: 580, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 16px', borderBottom: '1px solid #F3F4F6', flexShrink: 0 },
  title: { margin: 0, fontSize: 17, fontWeight: 800, color: '#111827' },
  closeBtn: { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6B7280' },
  body: { padding: '20px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em' },
  catGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8 },
  catBtn: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
    padding: '12px 8px', border: '2px solid', borderRadius: 12,
    cursor: 'pointer', transition: 'all 0.15s', background: 'white',
  },
  prioBtn: { flex: 1, padding: '8px', border: '2px solid', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 13 },
  input: { padding: '10px 14px', border: '2px solid #E5E7EB', borderRadius: 10, fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' },
  textarea: { padding: '10px 14px', border: '2px solid #E5E7EB', borderRadius: 10, fontSize: 14, resize: 'vertical', outline: 'none', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  select: { padding: '10px 14px', border: '2px solid #E5E7EB', borderRadius: 10, fontSize: 14, outline: 'none', background: 'white', width: '100%' },
  dosCols: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  alertaUrgente: { background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#991B1B' },
  footer: { display: 'flex', gap: 10, padding: '16px 24px', borderTop: '1px solid #F3F4F6', justifyContent: 'flex-end', flexShrink: 0 },
  btnSec: { padding: '10px 20px', border: '2px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151' },
  btnPri: { padding: '10px 24px', border: 'none', borderRadius: 8, color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700, transition: 'opacity 0.15s' },
};

// ─────────────────────────────────────────
// PÁGINA PRINCIPAL: NOVEDADES
// ─────────────────────────────────────────
export default function Novedades() {
  const { usuario, puedeVer } = useAuth();
  const esAdmin = puedeVer('gerencia', 'administracion');

  const [tickets, setTickets]       = useState([]);
  const [total, setTotal]           = useState(0);
  const [cargando, setCargando]     = useState(true);
  const [ticketActivo, setTicketActivo] = useState(null);
  const [modalNuevo, setModalNuevo] = useState(false);
  const [bodegas, setBodegas]       = useState([]);
  const [areas, setAreas]           = useState([]);

  const [filtros, setFiltros] = useState({
    estado: '',
    categoria: '',
    prioridad: '',
    pagina: 1,
  });

  // Cargar catálogos
  useEffect(() => {
    Promise.all([obtenerBodegas(), obtenerAreas()]).then(([b, a]) => {
      setBodegas(b.data);
      setAreas(a.data);
    });
  }, []);

  // Cargar tickets
  const cargarTickets = useCallback(() => {
    setCargando(true);
    const params = {};
    if (filtros.estado)    params.estado    = filtros.estado;
    if (filtros.categoria) params.categoria = filtros.categoria;
    if (filtros.prioridad) params.prioridad = filtros.prioridad;
    params.pagina = filtros.pagina;

    listarTickets(params)
      .then(r => {
        setTickets(r.data.tickets || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => toast.error('Error cargando novedades'))
      .finally(() => setCargando(false));
  }, [filtros]);

  useEffect(() => { cargarTickets(); }, [cargarTickets]);

  const setFiltro = (k, v) => setFiltros(f => ({ ...f, [k]: v, pagina: 1 }));

  // Contadores por estado
  const countsPorEstado = ESTADOS.reduce((acc, e) => {
    acc[e.value] = tickets.filter(t => t.estado === e.value).length;
    return acc;
  }, {});
  const urgentes = tickets.filter(t => t.prioridad === 'urgente' || t.categoria === 'urgente').length;

  return (
    <div style={pg.root}>
      {/* Header */}
      <div style={pg.header}>
        <div>
          <h1 style={pg.titulo}><span>🔧</span> Novedades</h1>
          <p style={pg.subtitulo}>{total} novedad{total !== 1 ? 'es' : ''} registrada{total !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setModalNuevo(true)} style={pg.btnNuevo}>
          + Nueva Novedad
        </button>
      </div>

      {/* Alerta urgentes */}
      {urgentes > 0 && (
        <div style={pg.alertaUrgente}>
          <span style={{ fontSize: 20 }}>🚨</span>
          <span><strong>{urgentes} novedad{urgentes > 1 ? 'es' : ''} urgente{urgentes > 1 ? 's' : ''}</strong> requiere{urgentes > 1 ? 'n' : ''} atención inmediata</span>
          <button onClick={() => setFiltro('prioridad', filtros.prioridad === 'urgente' ? '' : 'urgente')} style={pg.btnFiltrarUrgente}>
            {filtros.prioridad === 'urgente' ? 'Ver todas' : 'Filtrar urgentes'}
          </button>
        </div>
      )}

      <div style={pg.layout}>
        {/* ── Panel izquierdo: lista ── */}
        <div style={pg.panelLista}>
          {/* Filtros */}
          <div style={pg.filtros}>
            <div style={pg.filtroGroup}>
              <label style={pg.filtroLabel}>Estado</label>
              <div style={pg.filtroRow}>
                <button onClick={() => setFiltro('estado', '')} style={{ ...pg.filtroBtn, ...(filtros.estado === '' ? pg.filtroBtnActive : {}) }}>Todos</button>
                {ESTADOS.map(e => (
                  <button
                    key={e.value}
                    onClick={() => setFiltro('estado', filtros.estado === e.value ? '' : e.value)}
                    style={{
                      ...pg.filtroBtn,
                      ...(filtros.estado === e.value ? { background: e.bg, color: e.color, borderColor: e.color } : {}),
                    }}
                  >
                    {e.label}
                    {countsPorEstado[e.value] > 0 && (
                      <span style={{ ...pg.filtroBadge, background: e.bg, color: e.color }}>{countsPorEstado[e.value]}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div style={pg.filtroGroup}>
              <label style={pg.filtroLabel}>Categoría</label>
              <div style={pg.filtroRow}>
                <button onClick={() => setFiltro('categoria', '')} style={{ ...pg.filtroBtn, ...(filtros.categoria === '' ? pg.filtroBtnActive : {}) }}>Todas</button>
                {CATEGORIAS.map(c => (
                  <button
                    key={c.value}
                    onClick={() => setFiltro('categoria', filtros.categoria === c.value ? '' : c.value)}
                    style={{
                      ...pg.filtroBtn,
                      ...(filtros.categoria === c.value ? { background: c.bg, color: c.color, borderColor: c.color } : {}),
                    }}
                  >
                    {c.icon} {c.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Lista */}
          <div style={pg.lista}>
            {cargando ? (
              <div style={pg.loading}>Cargando novedades...</div>
            ) : tickets.length === 0 ? (
              <div style={pg.vacio}>
                <span style={{ fontSize: 40 }}>🎉</span>
                <p>Sin novedades{filtros.estado || filtros.categoria ? ' con estos filtros' : ' pendientes'}</p>
              </div>
            ) : (
              tickets.map(t => (
                <TarjetaTicket
                  key={t.id}
                  ticket={t}
                  onClick={() => setTicketActivo(t.id)}
                  activo={ticketActivo === t.id}
                />
              ))
            )}
          </div>
        </div>

        {/* ── Panel derecho: detalle ── */}
        <div style={{ ...pg.panelDetalle, display: ticketActivo ? 'flex' : 'none' }}>
          {ticketActivo && (
            <DetalleTicket
              ticketId={ticketActivo}
              onClose={() => setTicketActivo(null)}
              onUpdated={cargarTickets}
              esAdmin={esAdmin}
            />
          )}
        </div>

        {/* Placeholder cuando no hay ticket seleccionado */}
        {!ticketActivo && (
          <div style={pg.panelDetallePlaceholder}>
            <span style={{ fontSize: 40 }}>👆</span>
            <p>Selecciona una novedad para ver el detalle y comentar</p>
          </div>
        )}
      </div>

      {/* Modal nuevo ticket */}
      {modalNuevo && (
        <ModalNuevoTicket
          bodegas={bodegas}
          areas={areas}
          onClose={() => setModalNuevo(false)}
          onCreado={cargarTickets}
        />
      )}
    </div>
  );
}

const pg = {
  root: { fontFamily: "'Segoe UI', sans-serif" },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 },
  titulo: { margin: 0, fontSize: 26, fontWeight: 800, color: '#111827', display: 'flex', alignItems: 'center', gap: 10 },
  subtitulo: { margin: '4px 0 0', color: '#6B7280', fontSize: 14 },
  btnNuevo: { padding: '11px 22px', border: 'none', borderRadius: 10, background: 'linear-gradient(135deg,#1a1a2e,#16213e)', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700, boxShadow: '0 4px 12px rgba(26,26,46,0.3)' },
  alertaUrgente: { display: 'flex', alignItems: 'center', gap: 12, background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 14, color: '#DC2626', flexWrap: 'wrap' },
  btnFiltrarUrgente: { marginLeft: 'auto', padding: '6px 14px', border: '1px solid #EF4444', borderRadius: 8, background: 'white', color: '#EF4444', cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  layout: { display: 'grid', gridTemplateColumns: '1fr 420px', gap: 16, alignItems: 'start' },
  panelLista: { display: 'flex', flexDirection: 'column', gap: 8 },
  filtros: { background: 'white', borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
  filtroGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  filtroLabel: { fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em' },
  filtroRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  filtroBtn: { padding: '5px 12px', border: '1.5px solid #E5E7EB', borderRadius: 20, background: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.12s' },
  filtroBtnActive: { background: '#1a1a2e', color: 'white', borderColor: '#1a1a2e' },
  filtroBadge: { padding: '1px 6px', borderRadius: 10, fontSize: 10, fontWeight: 800 },
  lista: { display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' },
  loading: { padding: '40px 20px', textAlign: 'center', color: '#9CA3AF' },
  vacio: { padding: '40px 20px', textAlign: 'center', color: '#9CA3AF', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 },
  panelDetalle: {
    background: 'white', borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    position: 'sticky', top: 20, height: 'calc(100vh - 140px)',
    flexDirection: 'column', overflow: 'hidden',
  },
  panelDetallePlaceholder: {
    background: 'white', borderRadius: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    height: 'calc(100vh - 140px)', position: 'sticky', top: 20,
    color: '#9CA3AF', gap: 8, fontSize: 14,
  },
};
